import React, { useEffect, useState } from "react";
import Button from "../../ui/Button";
import ModalInput from "../../ui/ModalTextInput";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { updateUserProfileAsync } from "../../../redux/features/adminSlice";
import { RootState } from "../../../redux/app/store";
import { editAdminValidationSchema } from "./schemas";

interface FormDataProps {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phoneNumber: string;
  roles: string;
  address: string;
}

interface EditAdminModalProps {
  open: boolean;
  onClose: () => void;
  selectedAdmin: any;
}

const EditAdminModal: React.FC<EditAdminModalProps> = ({
  open,
  onClose,
  selectedAdmin,
}) => {
  const dispatch = useAppDispatch();

  const [formValues, setFormValues] = useState<FormDataProps>({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    phoneNumber: "",
    roles: "",
    address: "",
  });

  const [errors, setErrors] = useState<Partial<FormDataProps>>({});
  const { isLoading } = useAppSelector((state: RootState) => state.admin);

  useEffect(() => {
    if (selectedAdmin) {
      const [firstName, lastName] = selectedAdmin.fullName.trim().split(" ");
      setFormValues({
        firstName: firstName ?? "",
        lastName: lastName ?? "",
        username: selectedAdmin.username ?? "",
        email: selectedAdmin.email ?? "",
        phoneNumber: selectedAdmin.phoneNumber ?? "",
        roles: selectedAdmin.roles ?? "",
        address: selectedAdmin.address ?? "",
      });
    }
  }, [selectedAdmin]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleValidation = async () => {
    try {
      await editAdminValidationSchema.validate(formValues, {
        abortEarly: false,
      });
      setErrors({});
      return true;
    } catch (err: any) {
      const newErrors: any = {};
      err.inner.forEach((error: any) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await handleValidation();
    if (isValid) {
      dispatch(updateUserProfileAsync({ id: selectedAdmin.id, ...formValues }))
        .unwrap()
        .then(() => {
          toast.success("Admin updated successfully");
          onClose();
        })
        .catch((error) => {
          console.error("Failed to update admin:", error);
          toast.error("Failed to update admin");
        });
    }
  };

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Edit Admin"
      footer={
        <div className="flex justify-between relative bottom-0">
          <Button
            type="button"
            onClick={onClose}
            label="Discard"
            customStyle={{
              width: "auto",
              minWidth: "150px",
              background: "none",
              color: "#000",
            }}
          />
          <Button
            label="Done"
            type="button"
            onClick={handleSubmit}
            customStyle={{
              width: "auto",
              minWidth: "150px",
            }}
            isLoading={isLoading}
          />
        </div>
      }
      open={open}
      onClose={onClose}
    >
      <div className="p-6">
        <ModalInput
          label="firstName"
          placeholder="Enter firstName"
          name="firstName"
          value={formValues.firstName}
          onChange={handleInputChange}
          error={errors.firstName}
          required
        />
        <ModalInput
          label="lastName"
          placeholder="Enter lastName"
          name="lastName"
          value={formValues.lastName}
          onChange={handleInputChange}
          error={errors.lastName}
          required
        />
        <ModalInput
          label="Username"
          placeholder="Enter username"
          name="username"
          value={formValues.username}
          onChange={handleInputChange}
          error={errors.username}
          required
        />
        <ModalInput
          label="Email Address"
          type="email"
          placeholder="Enter email"
          name="email"
          value={formValues.email}
          onChange={handleInputChange}
          error={errors.email}
          required
        />
        <ModalInput
          label="Phone number"
          type="tel"
          placeholder="Enter phone number"
          name="phoneNumber"
          value={formValues.phoneNumber}
          onChange={handleInputChange}
          error={errors.phoneNumber}
          required
        />
        {/* Optionally, add dropdowns for roles and address here */}
      </div>
    </CustomModalContainer>
  );
};

export default EditAdminModal;
